import { h } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect, useState } from 'preact/hooks'
import { getAttachmentKind, getFileCategory, isBrowserRenderableImage } from '@tshmanager/chat-shared'
import { chatApi } from '../chat-api'

const MAX_WIDTH  = 220
const MAX_HEIGHT = 320
const MIN_HEIGHT = 72
const DEFAULT_AR = 4 / 3

/**
 * dimensionsFor — calculates display dimensions for a chat attachment image.
 * Ported from chat_attachment_image_controller.js.
 *
 * @param {number} aspectRatio  width / height (0 or negative → DEFAULT_AR)
 * @returns {{ width: number, height: number }}
 */
export function dimensionsFor(aspectRatio) {
  const ar = aspectRatio > 0 ? aspectRatio : DEFAULT_AR

  let width  = MAX_WIDTH
  let height = width / ar

  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT
    width  = height * ar
  }

  if (height < MIN_HEIGHT) {
    height = MIN_HEIGHT
    width  = height * ar
    if (width > MAX_WIDTH) {
      width  = MAX_WIDTH
      height = width / ar
    }
  }

  return { width: Math.round(width), height: Math.round(height) }
}

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024)        return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * AttachmentView — renders a single message attachment.
 *
 * Props: { attachment }
 *
 * attachment shape:
 *   { url, download_url, filename, content_type, byte_size,
 *     is_image, width, height, aspect_ratio,
 *     lightbox_url, lightbox_width,
 *     room_id }
 */
export function AttachmentView({ attachment, roomId, messageId }) {
  if (!attachment) return null

  // Poll for a server-generated preview once the backend signals preview_pending.
  // Starts with the initial attachment; swaps to the fresh copy once preview_url appears.
  const resolved = usePendingPreview(attachment, roomId, messageId)
  const att = resolved || attachment

  const { url, download_url, filename, content_type, byte_size } = att
  // Rendering kind is decided purely by content type — mutually exclusive.
  // Preview readiness (preview_url / preview_pending) is handled separately below,
  // only within the 'document' kind.
  const kind    = getAttachmentKind({ ...att, content_type: content_type || '' })
  const isImage = kind === 'image'
  const isVideo = kind === 'video'
  const isDocument = kind === 'document'
  const isPdf   = content_type === 'application/pdf'
  // Browser-renderable image (jpeg/png/…): paint directly. HEIC/HEIF cannot be
  // painted by the browser, so treat them like documents — wait for the server
  // JPEG preview (preview_url) and show a file chip until it arrives.
  const browserImage   = isImage && isBrowserRenderableImage({ ...att, content_type: content_type || '' })
  const nonBrowserImage = isImage && !browserImage
  // A non-browser image is "ready" once its converted preview URL is available.
  const nonBrowserImageReady = nonBrowserImage && !!att.preview_url
  const hasPreview     = isDocument && !!att.preview_url
  const previewPending = (isDocument || nonBrowserImage) && !att.preview_url && att.preview_pending === true
  const size    = formatSize(byte_size)
  const dlUrl   = download_url || url || '#'

  if (!url) {
    // Blob not yet available — pending placeholder
    if (browserImage) {
      const ar   = att.aspect_ratio || (att.width && att.height ? att.width / att.height : DEFAULT_AR)
      const dims = dimensionsFor(ar)
      return (
        <div
          class="chat-attachment-item"
        >
          <div
            class="chat-attachment-img-wrap chat-attachment-img-wrap--pending"
            style={`width:${dims.width}px;aspect-ratio:${dims.width} / ${dims.height}`}
          >
            <i class="bi bi-hourglass-split chat-attachment-hourglass" aria-hidden="true" />
          </div>
        </div>
      )
    }
    return (
      <div class="chat-attachment-item">
        <div class="chat-attachment-file">
          <div class="chat-attachment-file-icon">
            <i class="bi bi-hourglass-split text-secondary" />
          </div>
          <div class="chat-attachment-file-info">
            <span class="chat-attachment-file-name">{filename || 'Feltöltés folyamatban…'}</span>
          </div>
        </div>
      </div>
    )
  }

  // Browser-renderable image → inline image. A HEIC/HEIF whose converted preview
  // is ready also renders as an image (ImageAttachment uses preview_url as src).
  if (browserImage || nonBrowserImageReady) {
    return <ImageAttachment attachment={att} dlUrl={dlUrl} filename={filename} />
  }

  // Preview PNG ready → rich document preview.
  if (hasPreview) {
    return <DocumentPreviewAttachment attachment={att} dlUrl={dlUrl} filename={filename} size={size} isPdf={isPdf} />
  }

  // Preview still generating (document PNG or HEIC→JPEG conversion) → file-chip
  // fallback with a subtle spinner while we poll for the converted preview.
  if (previewPending) {
    const cat = getFileCategory(filename || '')
    const iconClass = fileChipIcon(cat)
    return (
      <div class="chat-attachment-item">
        <div class="chat-attachment-file">
          <div class="chat-attachment-file-icon">
            <i class={`bi ${iconClass}`} />
          </div>
          <div class="chat-attachment-file-info">
            <span class="chat-attachment-file-name">{filename}</span>
            {size && <span class="chat-attachment-file-size">{size}</span>}
          </div>
          <span class="chat-attachment-file-dl" title="Előnézet készül…">
            <span class="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
          </span>
        </div>
      </div>
    )
  }

  // Document without a preview PNG (generation failed / not previewable) → file chip.
  // PDFs keep the inline iframe (PhotoSwipe) link on the icon.
  if (isDocument) {
    const cat = getFileCategory(filename || '')
    const iconClass = fileChipIcon(cat)
    return (
      <div class="chat-attachment-item">
        <div class="chat-attachment-file">
          <a
            href={isPdf ? url : dlUrl}
            class="chat-attachment-file-icon"
            data-photoswipe={isPdf && att.room_id ? `chat-room-${att.room_id}` : undefined}
            data-type={isPdf ? 'iframe' : undefined}
            data-caption={isPdf ? filename : undefined}
            target={isPdf ? undefined : '_blank'}
            rel={isPdf ? undefined : 'noopener'}
            data-turbo="false"
          >
            <i class={`bi ${iconClass}`} />
          </a>
          <div class="chat-attachment-file-info">
            <span class="chat-attachment-file-name">{filename}</span>
            {size && <span class="chat-attachment-file-size">{size}</span>}
          </div>
          <a
            href={dlUrl}
            class="chat-attachment-file-dl"
            title="Letöltés"
            target="_blank"
            rel="noopener"
            download={filename}
            data-turbo="false"
          >
            <i class="bi bi-download" />
          </a>
        </div>
      </div>
    )
  }

  if (isVideo) {
    return (
      <div class="chat-attachment-item">
        <div class="chat-attachment-video-wrap">
          <video class="chat-attachment-video rounded" controls preload="metadata">
            <source src={url} type={content_type} />
          </video>
          <a
            href={dlUrl}
            class="chat-attachment-video-download"
            title={filename}
            target="_blank"
            rel="noopener"
            download={filename}
            data-turbo="false"
          >
            <i class="bi bi-download" />
          </a>
        </div>
      </div>
    )
  }

  // Generic file chip — icon driven by file category
  const cat = getFileCategory(filename || '')
  const fileIconClass = fileChipIcon(cat)

  return (
    <div class="chat-attachment-item">
      <div class="chat-attachment-file">
        <div class="chat-attachment-file-icon">
          <i class={`bi ${fileIconClass}`} />
        </div>
        <div class="chat-attachment-file-info">
          <span class="chat-attachment-file-name">{filename}</span>
          {size && <span class="chat-attachment-file-size">{size}</span>}
        </div>
        <a
          href={dlUrl}
          class="chat-attachment-file-dl"
          title="Letöltés"
          target="_blank"
          rel="noopener"
          download={filename}
          data-turbo="false"
        >
          <i class="bi bi-download" />
        </a>
      </div>
    </div>
  )
}

// Bootstrap-icon class per file category, shared by the chip renderers.
function fileChipIcon(cat) {
  return {
    pdf:   'bi-file-earmark-pdf text-danger',
    word:  'bi-file-earmark-word text-primary',
    excel: 'bi-file-earmark-excel text-success',
    ppt:   'bi-file-earmark-slides text-warning',
    audio: 'bi-file-earmark-music text-purple',
    zip:   'bi-file-earmark-zip text-warning',
    code:  'bi-file-earmark-code text-info',
    text:  'bi-file-earmark-text text-secondary',
    image: 'bi-file-earmark-image text-primary',
    video: 'bi-file-earmark-play text-danger',
  }[cat] || 'bi-file-earmark text-secondary'
}

/**
 * usePendingPreview — polls the message endpoint until a document attachment's
 * server-generated preview PNG is ready, then returns the refreshed attachment
 * (with preview_url). Returns null while no fresher copy is available.
 */
function usePendingPreview(attachment, roomId, messageId) {
  const [fresh, setFresh] = useState(null)

  useEffect(() => {
    // Only poll for real (persisted) messages whose preview is still generating.
    if (!attachment || attachment.preview_url || attachment.preview_pending !== true) return
    if (roomId == null || messageId == null || messageId < 0) return

    let cancelled = false
    let attempts  = 0
    let timer     = null
    const MAX_ATTEMPTS = 20   // ~2 min at 6s spacing
    const INTERVAL_MS  = 6000

    const tick = async () => {
      if (cancelled) return
      attempts += 1
      try {
        const res = await chatApi.messages.show(roomId, messageId)
        const msg = await res.json
        const updated = (msg?.attachments || []).find(a => a.id === attachment.id)
        if (updated?.preview_url) {
          if (!cancelled) {
            setFresh(updated)
            // The chip (~60px) grows into a ~280px preview card. The message
            // list watches its content wrapper with a ResizeObserver, so this
            // height change is picked up automatically and re-pins to the bottom
            // if the user was already there — no manual event needed.
          }
          return
        }
        if (updated && updated.preview_pending === false && !updated.preview_url) {
          // Generation failed / not previewable — stop polling AND publish the
          // updated (non-pending) copy so the render falls back to a static
          // chip/icon instead of leaving the original `attachment` prop's
          // stale `preview_pending: true` stuck on screen forever (the eternal
          // spinner bug: polling silently gave up, but nothing told the UI).
          if (!cancelled) setFresh(updated)
          return
        }
      } catch (_e) {
        // transient — keep polling until the attempt cap
      }
      if (cancelled) return
      if (attempts < MAX_ATTEMPTS) {
        timer = setTimeout(tick, INTERVAL_MS)
      } else {
        // Attempt cap reached with no definitive answer (e.g. persistent network
        // errors) — force preview_pending off so the fallback chip/icon renders
        // instead of spinning forever.
        setFresh({ ...attachment, preview_pending: false })
      }
    }

    timer = setTimeout(tick, INTERVAL_MS)
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [attachment?.id, attachment?.preview_url, attachment?.preview_pending, roomId, messageId])

  return fresh
}

/** Document preview — server-generated PNG thumbnail with a filename overlay. */
function DocumentPreviewAttachment({ attachment, dlUrl, filename, size, isPdf }) {
  const cat = getFileCategory(filename || '')
  const fileIconClass = fileChipIcon(cat)

  const previewHref = isPdf ? attachment.url : dlUrl

  return (
    <div class="chat-attachment-item">
      <div class="chat-attachment-doc-preview">
        <a
          href={previewHref}
          class="chat-attachment-doc-preview-thumb-link"
          data-photoswipe={isPdf && attachment.room_id ? `chat-room-${attachment.room_id}` : undefined}
          data-type={isPdf ? 'iframe' : undefined}
          data-caption={isPdf ? filename : undefined}
          data-turbo="false"
          target={isPdf ? undefined : '_blank'}
          rel={isPdf ? undefined : 'noopener'}
        >
          <img
            src={attachment.preview_url}
            class="chat-attachment-doc-preview-thumb"
            alt={filename}
            loading="lazy"
          />
          <div class="chat-attachment-doc-preview-overlay">
            <i class={`bi ${fileIconClass} chat-attachment-doc-preview-overlay-icon`} />
            <div class="chat-attachment-doc-preview-overlay-info">
              <span class="chat-attachment-doc-preview-overlay-name">{filename}</span>
              {size && <span class="chat-attachment-doc-preview-overlay-size">{size}</span>}
            </div>
          </div>
        </a>
        <a
          href={dlUrl}
          class="chat-attachment-doc-preview-dl"
          title="Letöltés"
          target="_blank"
          rel="noopener"
          download={filename}
          data-turbo="false"
        >
          <i class="bi bi-download" />
        </a>
      </div>
    </div>
  )
}

/** Image attachment — computes dimensions, renders PhotoSwipe-ready link. */
function ImageAttachment({ attachment, dlUrl, filename }) {
  // Blob metadata dimensions (original image size) — available after Active Storage analyze.
  const naturalWidthSig  = useSignal(attachment.width  || 0)
  const naturalHeightSig = useSignal(attachment.height || 0)
  const [loadFailed, setLoadFailed] = useState(false)

  const ar   = naturalWidthSig.value && naturalHeightSig.value
    ? naturalWidthSig.value / naturalHeightSig.value
    : attachment.aspect_ratio || DEFAULT_AR
  const dims = dimensionsFor(ar)

  // Show the lightweight preview thumbnail in the bubble — load the full
  // original only when the user opens PhotoSwipe.
  const thumbSrc = attachment.preview_url || attachment.url

  function handleLoad(e) {
    setLoadFailed(false)
    const img = e.target
    if (!img.naturalWidth || !img.naturalHeight) return
    // Only update from the loaded image's natural size when we don't already
    // have blob metadata — avoids overwriting correct original dims with
    // the (smaller) thumbnail dimensions.
    if (!attachment.width || !attachment.height) {
      naturalWidthSig.value  = img.naturalWidth
      naturalHeightSig.value = img.naturalHeight
    }
  }

  // Pass the original image dimensions to PhotoSwipe so it can render the
  // full-res image at the correct size without fetching it first.
  // If blob metadata is missing, omit the attributes so PhotoSwipe
  // probes the dimensions itself.
  const pswpWidth  = naturalWidthSig.value  || undefined
  const pswpHeight = naturalHeightSig.value || undefined

  // Conditional responsive srcset — present only for images that exceeded
  // PreviewProcessorJob::LARGE_IMAGE_EDGE_PX (see Chat::AttachmentPreview.serialize's
  // lightbox_url/lightbox_width). The largest candidate is always the original
  // (attachment.url), so zooming in always shows the true, full-resolution image —
  // never an artificially capped size. Small/normal images have no lightbox_url,
  // so no srcset is added — identical to today's behavior.
  const pswpSrcset = attachment.lightbox_url && pswpWidth
    ? `${attachment.lightbox_url} ${attachment.lightbox_width}w, ${attachment.url} ${pswpWidth}w`
    : undefined

  if (loadFailed) {
    const cat = getFileCategory(filename || '')
    const iconClass = fileChipIcon(cat)
    return (
      <div class="chat-attachment-item">
        <div class="chat-attachment-file">
          <a
            href={dlUrl}
            class="chat-attachment-file-icon"
            target="_blank"
            rel="noopener"
            download={filename}
            data-turbo="false"
          >
            <i class={`bi ${iconClass}`} />
          </a>
          <div class="chat-attachment-file-info">
            <span class="chat-attachment-file-name">{filename || 'Kép'}</span>
            <span class="chat-attachment-file-size text-danger">Nem sikerült betölteni</span>
          </div>
          <a
            href={dlUrl}
            class="chat-attachment-file-dl"
            title="Letöltés"
            target="_blank"
            rel="noopener"
            download={filename}
            data-turbo="false"
          >
            <i class="bi bi-download" />
          </a>
        </div>
      </div>
    )
  }

  return (
    <div class="chat-attachment-item">
      <div
        class="chat-attachment-img-wrap"
        style={`width:${dims.width}px;aspect-ratio:${dims.width} / ${dims.height}`}
      >
        <a
          href={attachment.url}
          class="chat-attachment-img-link"
          data-photoswipe={attachment.room_id ? `chat-room-${attachment.room_id}` : undefined}
          data-pswp-src={attachment.url}
          data-pswp-srcset={pswpSrcset}
          data-pswp-width={pswpWidth}
          data-pswp-height={pswpHeight}
          data-type="image"
          data-caption={filename}
          data-download-url={dlUrl}
          data-turbo="false"
        >
          <img
            src={thumbSrc}
            class="chat-attachment-image rounded"
            loading="lazy"
            alt={filename}
            onLoad={handleLoad}
            onError={() => { setLoadFailed(true) }}
          />
        </a>
        <a
          href={dlUrl}
          class="chat-attachment-img-download"
          title="Letöltés"
          target="_blank"
          rel="noopener"
          download={filename}
          data-turbo="false"
        >
          <i class="bi bi-download" />
        </a>
      </div>
    </div>
  )
}
